import { MarketCode } from '@tink/config/src/markets';
import { ConfigurationFeatureFlag, ConfigurationModel } from '@tink/config/src/tinkLinkConfig';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';

import { isInTestGroup } from '../utilities/ab-testing-group-calculator';
import { CredentialsType } from './credentials';
import { Version } from './version';
import { ProviderCapability } from './providerCapability';
import { haveFeatureFlag, isBlockListedHandoffEnabled, isHandoffEnabled } from './configuration';

export enum ProviderStatus {
  ENABLED = 'ENABLED',
  TEMPORARY_DISABLED = 'TEMPORARY_DISABLED',
}

export enum ProviderType {
  BANK = 'BANK',
  BROKER = 'BROKER',
  CREDIT_CARD = 'CREDIT_CARD',
  OTHER = 'OTHER',
  TEST = 'TEST',
  FRAUD = 'FRAUD',
  FIRST_PARTY = 'FIRST_PARTY',
}

export enum ProviderAccessType {
  OPEN_BANKING = 'OPEN_BANKING',
  OTHER = 'OTHER',
}

export enum AuthenticationUserType {
  PERSONAL = 'PERSONAL',
  BUSINESS = 'BUSINESS',
  CORPORATE = 'CORPORATE',
}

export enum ProviderHealthStatus {
  HEALTHY = 'HEALTHY',
  PLANNED_MAINTENANCE = 'PLANNED_MAINTENANCE',
  DEGRADED = 'DEGRADED',
  DOWN = 'DOWN',
}

export enum DataAccessProviders {
  MX = 'MX Technologies',
}

export type SelectOption = {
  text: string;
  value: string;
  iconUrl?: string;
};

export enum SupplementalInformationLayoutType {
  INSTRUCTIONS = 'INSTRUCTIONS',
}

export type ProviderFieldAdditionalInfo = {
  layoutType: SupplementalInformationLayoutType;
};

export type AdditionalInfoAccount = {
  accountName?: string;
  accountNumber: string;
  accountType?: string;
  balance?: number;
  currencyCode?: string;
  holderName?: string;
  iban?: string;
};

export type AdditionalInfo = AdditionalInfoAccount | ProviderFieldAdditionalInfo;

export type ProviderField = {
  additionalInfo: AdditionalInfo | null | string;
  checkbox: boolean;
  children: string | null;
  defaultValue: string | null;
  description: string;
  exposed: boolean;
  helpText: string | null;
  hint: string | null;
  immutable: boolean;
  masked: boolean;
  maxLength: number | null;
  minLength: number | null;
  name: string;
  numeric: boolean;
  optional: boolean;
  options: string | null;
  pattern: string | null;
  patternError: string | null;
  sensitive: boolean;
  type: string | null;
  value: string | null;
  selectOptions: SelectOption[] | null;
  style?: string | null;
  group?: string | null;
  oneOf?: boolean;
};

export enum ProviderAuthenticationFlow {
  EMBEDDED = 'EMBEDDED',
  REDIRECT = 'REDIRECT',
  DECOUPLED = 'DECOUPLED',
}

export type ProvidersApiResponse = {
  providers: ProviderEntity[];
};

export enum FinancialServicesSegment {
  PERSONAL = 'PERSONAL',
  BUSINESS = 'BUSINESS',
}

export type ProviderFinancialService = {
  segment: FinancialServicesSegment;
  shortName?: string;
};

export type ProviderEntity = {
  accessType: ProviderAccessType;
  authenticationUserType: AuthenticationUserType;
  capabilities: ProviderCapability[];
  credentialsType: CredentialsType;
  authenticationFlow?: ProviderAuthenticationFlow;
  currency: string;
  market: MarketCode;
  name: string;
  passwordHelpText: string;
  popular: boolean;
  displayName: string;
  displayDescription?: string;
  groupDisplayName: string;
  status: ProviderStatus;
  healthStatus?: {
    providerLogin: {
      status: ProviderHealthStatus;
    };
  };
  images: {
    banner: string | null;
    icon: string;
  };
  fields: ProviderField[];
  transactional: boolean;
  type: ProviderType;
  financialInstitutionId: string;
  financialInstitutionName: string;
  financialServices?: ProviderFinancialService[];
  loginHeaderColour?: string;
  rank: number;
};

export type ProviderConsent = {
  accountIds: string[];
  credentialsId: string;
  detailedError: {
    details: {
      reason: string;
      retryable: boolean;
    };
    displayMessage: string;
    type: string;
  };
  sessionExpiryDate: number;
  providerName: string;
  status: ProviderConsentStatus;
  statusUpdated?: number;
};

export type ProviderConsents = {
  providerConsents: ProviderConsent[];
};

export enum ProviderConsentStatus {
  CREATED = 'CREATED',
  AUTHENTICATING = 'AUTHENTICATING',
  AWAITING_MOBILE_BANKID_AUTHENTICATION = 'AWAITING_MOBILE_BANKID_AUTHENTICATION',
  AWAITING_SUPPLEMENTAL_INFORMATION = 'AWAITING_SUPPLEMENTAL_INFORMATION',
  UPDATING = 'UPDATING',
  UPDATED = 'UPDATED',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  TEMPORARY_ERROR = 'TEMPORARY_ERROR',
  PERMANENT_ERROR = 'PERMANENT_ERROR',
  AWAITING_THIRD_PARTY_APP_AUTHENTICATION = 'AWAITING_THIRD_PARTY_APP_AUTHENTICATION',
  DELETED = 'DELETED',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
}

export const Provider = {
  isThirdPartyAppType: (provider: ProviderEntity) => {
    return provider.credentialsType === CredentialsType.THIRD_PARTY_APP;
  },

  isFineco: (provider: ProviderEntity) => {
    const finecoProviderNames = ['it-finecobank-oauth2'];

    return finecoProviderNames.includes(provider.name);
  },

  isSplitFieldsProvider: (provider: ProviderEntity) => {
    // Only using for temporary testing in Germany.
    // Remove this function when BE sends us split fields.
    const splitFieldProviders = ['de-deutschebank-ob', 'de-norisbank-ob'];

    return splitFieldProviders.includes(provider.name);
  },

  isMobileBankId: (provider: ProviderEntity) => {
    return provider.credentialsType === CredentialsType.MOBILE_BANKID;
  },

  isPassword: (provider: ProviderEntity) => {
    return provider.credentialsType === CredentialsType.PASSWORD;
  },

  isEnabled: (provider: ProviderEntity) => {
    return provider.status === ProviderStatus.ENABLED;
  },

  isUkOpenBanking: (provider?: ProviderEntity) => {
    return provider && provider.market === MarketCode.GB && Provider.isOpenBanking(provider);
  },

  isOpenBanking: (provider: ProviderEntity) => {
    return provider.accessType === ProviderAccessType.OPEN_BANKING;
  },

  hasFields: (provider: ProviderEntity) => {
    return provider.fields.length > 0;
  },

  isMarketSE: (provider: ProviderEntity) => {
    return provider.market === MarketCode.SE;
  },

  isMarketUK: (provider: ProviderEntity) => {
    return provider.market === MarketCode.GB;
  },

  isMarketIT: (provider: ProviderEntity) => {
    return provider.market === MarketCode.IT;
  },

  isMarketDE: (provider: ProviderEntity) => {
    return provider.market === MarketCode.DE;
  },

  isUnhealthy: (provider: ProviderEntity) => {
    const status = provider.healthStatus?.providerLogin?.status;

    return (
      status === ProviderHealthStatus.PLANNED_MAINTENANCE ||
      status === ProviderHealthStatus.DEGRADED
    );
  },

  isPlannedMaintenance: (provider: ProviderEntity) => {
    const status = provider.healthStatus?.providerLogin?.status;

    return status === ProviderHealthStatus.PLANNED_MAINTENANCE;
  },

  isDown: (provider: ProviderEntity) => {
    const status = provider.healthStatus?.providerLogin?.status;

    return status === ProviderHealthStatus.DOWN;
  },

  isTemporaryDisabled: (provider: ProviderEntity) => {
    const brokenSwedbankProviders = [
      'swedbank-bankid',
      'savingsbank-bankid',
      'swedbank-business-bankid',
      'savingsbank-business-bankid',
    ];

    if (userAgent.isMobile && brokenSwedbankProviders.includes(provider.name)) {
      return true;
    }

    return provider.status === ProviderStatus.TEMPORARY_DISABLED;
  },

  isBusiness: (provider: ProviderEntity) => {
    return provider.authenticationUserType === AuthenticationUserType.BUSINESS;
  },

  isTest: (provider: ProviderEntity) => {
    return provider.type === ProviderType.TEST;
  },

  isAcceptedType: (provider: ProviderEntity) => {
    const unwantedTypes = [ProviderType.FRAUD];

    return !unwantedTypes.includes(provider.type);
  },

  getIcon: (provider: ProviderEntity) => {
    return provider.images && provider.images.icon;
  },

  isRedirectAuthenticationFlow: (provider: ProviderEntity) => {
    return provider.authenticationFlow === ProviderAuthenticationFlow.REDIRECT;
  },

  isEmbeddedAuthenticationFlow: (provider: ProviderEntity) => {
    return provider.authenticationFlow === ProviderAuthenticationFlow.EMBEDDED;
  },

  isBlocklisted: (provider: ProviderEntity, version: string) => {
    // Global blocklist

    const blocklistedNames = ['be-demo', 'demo-multisupplemental'];

    if (blocklistedNames.includes(provider.name)) {
      return true;
    }

    const currentVersion = new Version(version);

    // Version blocklist
    if (currentVersion.lessThanOrEqualTo('0.4')) {
      const blockListedProvider = [
        'skandiabanken-ssn-bankid', // Temporarily disabling BankID providers using autostart token
        'se-alandsbanken-bankid', // Temporarily disabling BankID providers using autostart token
        'icabanken-bankid', // Temporarily disabling BankID providers using autostart token
        'sbab-bankid', // Temporarily disabling BankID providers using autostart token
      ];

      if (blockListedProvider.includes(provider.name)) {
        return true;
      }
    }

    return false;
  },

  isSwedbankOrSavingsbank: ({ name }: ProviderEntity) => {
    const swedbankAndSavingsbank = ['se-swedbank-fallback-token', 'se-savingsbank-fallback-token'];

    return name && swedbankAndSavingsbank.includes(name);
  },

  hasUniqueDisplayDescriptions: (providers: ProviderEntity[]) => {
    const uniqueDisplayDescriptions = new Set(
      providers.map((provider) => provider.displayDescription)
    );

    return uniqueDisplayDescriptions.size > 1;
  },
  /*
  This list enables providers to use the Handoff flow.
  These providers are here to support the handoff flow
  when mobile users choose the "Open on another device" method.
  On desktop the users will default to using the isRequiringAnimatedQRCode
  appraoch.
  */
  isRequiringHandoffQrCode: (
    providerName: string,
    providersRequiringHandoffQrCode: string[] = [
      'handelsbanken-business-bankid',
      'handelsbanken-bankid',
      'se-savingsbank-fallback',
      'se-swedbank-fallback',
      'se-swedbank-ob',
      'se-savingsbank-ob',
      'swedbank-bankid',
      'swedbank-business-bankid',
      'savingsbank-bankid',
      'savingsbank-business-bankid',
      'se-swedbank-business-ob',
      'se-savingsbank-business-ob',
    ]
  ) => {
    return providersRequiringHandoffQrCode.includes(providerName);
  },

  isRequiringAnimatedQRCode: (
    providerName: string,
    providersAnimatedQRCode: string[] = [
      'handelsbanken-desktop-bankid',
      'se-savingsbank-fallback',
      'se-swedbank-fallback',
      'se-swedbank-ob',
      'se-savingsbank-ob',
      'swedbank-bankid',
      'swedbank-business-bankid',
      'savingsbank-bankid',
      'savingsbank-business-bankid',
      'se-swedbank-business-ob',
      'se-savingsbank-business-ob',
      'se-seb-ob',
      'se-sparbankensyd-ob',
    ]
  ) => {
    return providersAnimatedQRCode.includes(providerName);
  },

  isRequiringPaymentConsentScreen: (
    providerName: string,
    providersPaymentConsentScreen: string[] = ['se-swedbank-ob', 'se-savingsbank-ob', 'se-seb-ob']
  ) => {
    return providersPaymentConsentScreen.includes(providerName);
  },

  hasLoginHeaderColour: (provider: ProviderEntity) => {
    return provider.loginHeaderColour;
  },
};

export const handoffFlowAvailable = (
  provider: ProviderEntity,
  configuration: ConfigurationModel,
  userId: string | undefined,
  abTestProviders: string[] = []
): boolean => {
  // Pre-requisites for running handoff flow
  if (
    !userAgent.isDesktop ||
    !isHandoffEnabled(configuration) ||
    !Provider.isOpenBanking(provider)
  ) {
    return false;
  }

  const blockListedNames = ['uk-starling-oauth2'];

  // Block listed providers are never enabled)
  if (blockListedNames.includes(provider.name)) {
    return false;
  }

  const blockListedFeatureFlaggedNames = ['uk-revolut-oauth2'];

  // Block listed providers with feature flag are disabled only if configuration doesn't have ENABLE_BLOCKLISTED_HANDOFF feature flag
  // Can be fully removed in the future if we will start offering Revolut with QR Handoff
  if (blockListedFeatureFlaggedNames.includes(provider.name)) {
    return isBlockListedHandoffEnabled(configuration);
  }

  const allowListDemo = [
    'uk-demobank-open-banking-handoff',
    'uk-demobank-open-banking-handoff-starling',
  ];

  const allowListUk = ['uk-monzo-oauth2'];

  const allowListIt = ['it-revolut-ob'];

  const allowListFr = [
    'fr-cic-ob',
    'fr-ing-ob',
    'fr-societegenerale-ob',
    'fr-lcl-ob',
    'fr-bnpparibas-ob',
    'fr-creditmutuel-oauth2',
    'fr-cmmgrandouest-ob',
    'fr-cmmlittoraldusudouest-ob',
    'fr-bpalsacelorrainechampagne-ob',
    'fr-bpaquitainecentreatlantique-ob',
    'fr-bpauvergneetrhonealpes-ob',
    'fr-bpbourgognefranchecomte-ob',
    'fr-bpgrandouest-ob',
    'fr-bpmediterranee-ob',
    'fr-bpoccitane-ob',
    'fr-bprivesdeparis-ob',
    'fr-bpsud-ob',
    'fr-bpvaldefrance-ob',
    'fr-bpdunord-ob',
    'fr-banquedesavoie-ob',
    'fr-boursorama-ob',
    'fr-labanquepostale-ob',
  ];

  const allowListNl = ['nl-ing-ob'];

  const allowListProviderNames: string[] = [
    ...allowListDemo,
    ...allowListUk,
    ...allowListFr,
    ...allowListNl,
    ...allowListIt,
  ];

  // Providers are enabled if they are allow listed
  if (allowListProviderNames.includes(provider.name)) {
    return true;
  }

  // Pre-requisites for running handoff flow
  if (Provider.isTest(provider)) {
    return false;
  }

  const abTestProvider = abTestProviders.find((names) => names === provider.name);

  if (userId && abTestProvider && isInTestGroup(0.5, [abTestProvider, userId, 'handoff-test'])) {
    return true;
  }

  const allowListedMarkets = ['GB'];

  // Providers are enabled if the market is allow listed
  if (allowListedMarkets.includes(provider.market)) {
    return true;
  }

  return false;
};

export const isRedirectingOnce = (provider: ProviderEntity): boolean => {
  const noDoubleRedirectProviders = [
    'fr-n26-ob',
    'de-n26-ob',
    'it-n26-ob',
    'es-n26-ob',
    'pt-n26-ob',
    'it-bancasella-ob',
  ];

  return noDoubleRedirectProviders.includes(provider.name);
};

export const isProlongedProvider = (
  market: MarketCode,
  configuration: ConfigurationModel
): boolean => {
  const prolongedMarkets = [MarketCode.IT, MarketCode.PT, MarketCode.US];

  return (
    prolongedMarkets.includes(market) ||
    haveFeatureFlag(configuration, ConfigurationFeatureFlag.SLOW_CREDENTIALS)
  );
};

export const isProlongedProviderPay = (provider: ProviderEntity): boolean => {
  const prolongedProviders = ['fr-lcl-ob', 'it-isp-oauth2'];

  return prolongedProviders.includes(provider.name);
};

export const filterByAccessType = (accessType: ProviderAccessType) => {
  return (providers: ProviderEntity[]) => {
    return providers.filter((p) => p.accessType === accessType);
  };
};

export const filterBySegment = (segment: FinancialServicesSegment) => {
  return (providers: ProviderEntity[]) => {
    return providers.filter((p) => {
      return p.financialServices?.find((fs) => fs.segment === segment);
    });
  };
};

export const filterByFinancialService = (financialService: ProviderFinancialService) => {
  return (providers: ProviderEntity[]) => {
    return providers.filter((p) => {
      return p.financialServices?.find(
        (fs) =>
          fs.segment === financialService.segment && fs.shortName === financialService.shortName
      );
    });
  };
};

type ProviderLookup = (providers: ProviderEntity[]) => ProviderEntity[];

export const searchProviders = (providers: ProviderEntity[], providerLookups: ProviderLookup[]) => {
  let foundProviders: ProviderEntity[] = providers;

  providerLookups.forEach((lookup) => {
    foundProviders = lookup(foundProviders);
  });

  return foundProviders;
};

export enum GermanStates {
  BADEN_WURTTEMBERG = 'Baden-Württemberg',
  BAYERN = 'Bayern',
  BERLIN = 'Berlin',
  BRANDENBURG = 'Brandenburg',
  BREMEN = 'Bremen',
  HAMBURG = 'Hamburg',
  HESSEN = 'Hessen',
  MECKLENBURG_VORPOMMERN = 'Mecklenburg-Vorpommern',
  NIEDERSACHSEN = 'Niedersachsen',
  NORDRHEIN_WESTFALEN = 'Nordrhein-Westfalen',
  RHEINLAND_PFALZ = 'Rheinland-Pfalz',
  SAARLAND = 'Saarland',
  SACHSEN = 'Sachsen',
  SACHSEN_ANHALT = 'Sachsen-Anhalt',
  SCHLESWIG_HOLSTEIN = 'Schleswig-Holstein',
  THURINGEN = 'Thüringen',
}

export const GERMAN_STATES = Object.values(GermanStates);
